// ============================================================================
// src/webforms/PigDailysWebform.jsx
// ----------------------------------------------------------------------------
// The legacy pig-dailys public webform (routed at /webform).
//
// 2026-04-29 (Phase 1C-B): no-photo submissions flow through the offline
// queue via useOfflineSubmit('pig_dailys').
//
// 2026-04-30 (Phase 1D-A): photo-attached submissions ALSO flow through
// the same hook. The hook's hasPhotos branch handles compress-once,
// atomic submission+photo_blobs enqueue on transient failure, and
// state:'stuck' on storage 401/403 or row schema-after-upload. The old
// "photos need a connection" copy is gone. Locked by
// tests/offline_queue_pig_dailys_photos.spec.js + the existing
// tests/pig_dailys_offline.spec.js (still locks the empty-photos flat
// path via the hook's short-circuit).
// ============================================================================
import React from 'react';
import {MAX_PHOTOS_PER_REPORT} from '../lib/dailyPhotos.js';
import {todayCentralISO} from '../lib/dateUtils.js';
import {useOfflineSubmit} from '../lib/useOfflineSubmit.js';
import type {SubmitState} from '../lib/useOfflineSubmit.js';
import {sb} from '../lib/supabase.js';
import {loadQueuedReportRecovery, recoveryText} from '../lib/offlineSubmissionRecovery.js';
import type {QueuedReportRecovery} from '../lib/offlineSubmissionRecovery.js';
import type {SessionSubmitter} from './webformsRoutes.js';
import DailyPhotoCapture from './DailyPhotoCapture.jsx';
import StuckSubmissionsModal, {payloadMember, payloadText} from './StuckSubmissionsModal.jsx';
import LockedSubmitter from './LockedSubmitter.jsx';

// A non-null, non-array object. Both blobs this form reads — the live webforms
// config and a queued submission's record — arrive as `unknown`, so every hop
// into them is proved rather than assumed.
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// `catch (e)` binds `unknown` under strict TypeScript. This is the file's own
// `e && e.message ? e.message : String(e)` idiom with that narrowing written out.
function errorMessage(e: unknown): string {
  if (typeof e === 'object' && e !== null && 'message' in e && typeof e.message === 'string' && e.message) {
    return e.message;
  }
  return String(e);
}

// One webform's configured fields, flattened out of the live config blob.
//
// The bridge hands `webformsConfig` over as `unknown` on purpose, exactly as
// the App-owned provider it replaced did: the admin panel edits the blob into
// shapes the seed in lib/defaults.ts never had, so the seed's type is not a
// description of what arrives at runtime.
// Fields therefore stay opaque records here — this form only ever tests
// `required`/`system` for truthiness, compares `enabled` to false, and matches
// `id` against four literals, none of which needs a declared member type.
function configuredFields(config: unknown, webformId: string): Record<string, unknown>[] {
  if (!isRecord(config)) return [];
  const webforms: unknown[] = Array.isArray(config.webforms) ? config.webforms : [];
  const wf = webforms.find((w) => isRecord(w) && w.id === webformId);
  if (!isRecord(wf)) return [];
  const sections: unknown[] = Array.isArray(wf.sections) ? wf.sections : [];
  return sections.flatMap((s) => {
    if (!isRecord(s) || !Array.isArray(s.fields)) return [];
    const fields: unknown[] = s.fields;
    return fields.filter(isRecord);
  });
}

// One entry of the active-pig-group dropdown. The component-scoped owner maps
// the jsonb array into value/label pairs, and this select reads exactly these
// two members — an entry carrying neither cannot render a usable option.
type WfGroupOption = {value: string; label: string};

function isWfGroupOption(value: unknown): value is WfGroupOption {
  return isRecord(value) && typeof value.value === 'string' && typeof value.label === 'string';
}

// The form's own state. Every numeric input is held as the raw input string and
// parsed at submit time, which is why the four number-ish members are strings.
type PigDailyForm = {
  date: string;
  teamMember: string;
  batchId: string;
  pigCount: string;
  feedLbs: string;
  groupMoved: boolean;
  nippleDrinkerMoved: boolean;
  nippleDrinkerWorking: boolean;
  troughsMoved: boolean;
  fenceWalked: boolean;
  fenceVoltage: string;
  issues: string;
};

// The five members wfTgl/wfToggle drive. Naming them is what keeps the computed
// assignment below writing a boolean into a boolean member.
type PigDailyToggleField =
  | 'groupMoved'
  | 'nippleDrinkerMoved'
  | 'nippleDrinkerWorking'
  | 'troughsMoved'
  | 'fenceWalked';

// What buildSubmitPayload hands to the offline queue: the pig_dailys column set
// plus, on the photo path, the raw File[] the hook compresses and sanitizes.
type PigDailyPayload = {
  date: string;
  team_member: string;
  batch_id: string;
  batch_label: string;
  pig_count: number | null;
  feed_lbs: number | null;
  group_moved: boolean;
  nipple_drinker_moved: boolean;
  nipple_drinker_working: boolean;
  troughs_moved: boolean;
  fence_walked: boolean;
  fence_voltage: number | null;
  issues: string | null;
  photos?: File[];
};

type PigDailysWebformProps = {
  sessionSubmitter?: SessionSubmitter;
  // Injected by PigDailysAuthenticatedBridge. Keep this raw because the
  // historical App sidecar passed raw values and this presenter owns the final
  // value/label narrowing immediately before rendering <option>s.
  activePigGroups?: readonly unknown[];
  // Also injected by PigDailysAuthenticatedBridge, through
  // webform.pigDailys.config. It used to be read straight off the App-owned
  // webforms-config provider, i.e. off App's global boot — do NOT reinstate
  // that provider hook here, and do NOT accept this value from WebformsEntry
  // as a prop. Both are channels tests/static/former_eager_evidence_static.test.js
  // scores as consumption, and either would make the `webform` app_store
  // retirement in tests/fixtures/former-eager-dependencies.json a false claim.
  //
  // `unknown` is what that provider handed over, and the narrowing stays in
  // configuredFields() below rather than moving to this boundary.
  webformsConfig?: unknown;
};

export default function PigDailysWebform({
  sessionSubmitter,
  activePigGroups = [],
  webformsConfig,
}: PigDailysWebformProps) {
  // Lane 1 CP1: every webform is login-required, so the submitter is always
  // the signed-in user.
  const lockedName = sessionSubmitter?.name || '';

  const [wfPhotos, setWfPhotos] = React.useState<File[]>([]);
  const [wfPhotoStatuses, setWfPhotoStatuses] = React.useState<string[]>([]);

  const [wfForm, setWfForm] = React.useState<PigDailyForm>(() => {
    return {
      date: todayCentralISO(),
      teamMember: sessionSubmitter?.name || '',
      batchId: '',
      pigCount: '',
      feedLbs: '',
      groupMoved: true,
      nippleDrinkerMoved: true,
      nippleDrinkerWorking: true,
      troughsMoved: true,
      fenceWalked: true,
      fenceVoltage: '',
      issues: '',
    };
  });
  const [wfSubmitting, setWfSubmitting] = React.useState(false);
  // 'none' | 'synced' | 'queued' | 'stuck' — Phase 1D-A adds 'stuck' for
  // storage 401/403 and row schema-after-photo-upload outcomes.
  // synced = row landed in pig_dailys.
  // queued = persisted in IDB; will replay on next online/tick/mount.
  // stuck  = persisted in IDB at retry budget exhausted; operator surfaces
  //          via stuck modal.
  const [wfDoneState, setWfDoneState] = React.useState<SubmitState | 'none'>('none');
  const [wfErr, setWfErr] = React.useState('');
  const [wfGroupName, setWfGroupName] = React.useState('');
  const [wfStuckOpen, setWfStuckOpen] = React.useState(false);
  const [recovery, setRecovery] = React.useState<QueuedReportRecovery | null>(null);
  const [recoveryLoading, setRecoveryLoading] = React.useState(false);
  const recoveryOperation = React.useRef<'opening' | 'submitting' | null>(null);
  const recoveryBusy = wfSubmitting || recoveryLoading;
  const priorDraft = React.useRef<{form: PigDailyForm; photos: File[]; done: SubmitState | 'none'} | null>(null);

  async function openQueued(csid: string) {
    if (recoveryOperation.current) return;
    recoveryOperation.current = 'opening';
    setRecoveryLoading(true);
    try {
      const loaded = await loadQueuedReportRecovery(sb, csid, 'pig_dailys');
      if (!recovery) priorDraft.current = {form: wfForm, photos: wfPhotos, done: wfDoneState};
      const r = loaded.record;
      setWfForm({
        date: recoveryText(r.date),
        teamMember: lockedName,
        batchId: recoveryText(r.batch_label),
        pigCount: recoveryText(r.pig_count),
        feedLbs: recoveryText(r.feed_lbs),
        groupMoved: r.group_moved === true,
        nippleDrinkerMoved: r.nipple_drinker_moved === true,
        nippleDrinkerWorking: r.nipple_drinker_working === true,
        troughsMoved: r.troughs_moved === true,
        fenceWalked: r.fence_walked === true,
        fenceVoltage: recoveryText(r.fence_voltage),
        issues: recoveryText(r.issues),
      });
      setWfPhotos(loaded.photos);
      setWfPhotoStatuses([]);
      setWfDoneState('none');
      setRecovery(loaded);
      setWfErr('');
      setWfStuckOpen(false);
    } catch (error) {
      setWfErr(errorMessage(error));
    } finally {
      recoveryOperation.current = null;
      setRecoveryLoading(false);
    }
  }

  function cancelRecovery() {
    if (recoveryOperation.current) return;
    if (priorDraft.current) {
      setWfForm(priorDraft.current.form);
      setWfPhotos(priorDraft.current.photos);
      setWfDoneState(priorDraft.current.done);
    }
    priorDraft.current = null;
    setRecovery(null);
    setWfErr('');
  }

  // Keep the submitter pinned to the signed-in identity.
  React.useEffect(() => {
    if (wfForm.teamMember !== lockedName) {
      setWfForm((prev) => ({...prev, teamMember: lockedName}));
    }
  }, [lockedName, wfForm.teamMember]);

  // Phase 1C-B no-photo offline queue + Phase 1D-A photo offline queue.
  // The hook handles both via the hasPhotos branch (offlineForms.js
  // pig_dailys.hasPhotos: true) — empty payload.photos short-circuits to
  // the flat path (locked by pig_dailys_offline.spec.js).
  const {submit, stuckRows, retryStuck, discardStuck} = useOfflineSubmit('pig_dailys');

  // Open the stuck modal automatically the first time we observe stuck rows.
  // Mirrors FuelSupply / AddFeed pattern.
  const initialStuckShownRef = React.useRef(false);
  React.useEffect(() => {
    if (stuckRows.length > 0 && !initialStuckShownRef.current) {
      initialStuckShownRef.current = true;
      setWfStuckOpen(true);
    }
  }, [stuckRows.length]);

  const wfGroupOptions = activePigGroups.filter(isWfGroupOption);

  function wfToggle(field: PigDailyToggleField, val: boolean) {
    setWfForm((f) => {
      const next = {...f};
      next[field] = val;
      return next;
    });
  }

  // Build a normalized payload from form state. Pure — no side effects.
  // The flat-insert registry consumes this and uses ids minted by the hook
  // for retry-stable replay.
  function buildSubmitPayload(): PigDailyPayload {
    return {
      date: wfForm.date,
      team_member: wfForm.teamMember.trim(),
      batch_id:
        recovery && wfForm.batchId === recovery.record.batch_label
          ? recoveryText(recovery.record.batch_id)
          : wfForm.batchId.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      batch_label: wfForm.batchId,
      pig_count: wfForm.pigCount !== '' ? parseInt(wfForm.pigCount) : null,
      feed_lbs: wfForm.feedLbs !== '' ? parseFloat(wfForm.feedLbs) : null,
      group_moved: wfForm.groupMoved,
      nipple_drinker_moved: wfForm.nippleDrinkerMoved,
      nipple_drinker_working: wfForm.nippleDrinkerWorking,
      troughs_moved: wfForm.troughsMoved,
      fence_walked: wfForm.fenceWalked,
      fence_voltage: wfForm.fenceVoltage !== '' ? parseFloat(wfForm.fenceVoltage) : null,
      issues: wfForm.issues.trim() || null,
    };
  }

  async function wfSubmit() {
    if (recoveryOperation.current) return;
    // Validate required fields based on webformsConfig. The config shape is
    // `webforms[].sections[].fields[]` — flatten before filtering. The
    // pre-1C-B code referenced `wfCfg.fields` directly, which was undefined
    // under the DEFAULT_WEBFORMS_CONFIG shape (sections-only). Latent
    // ReferenceError that surfaced under the offline-queue test harness
    // when the form path actually exercises submit; fixed inline.
    const wfRequiredFields = configuredFields(webformsConfig, 'pig-dailys').filter(
      (f) => f.required && f.enabled !== false,
    );
    if (!wfForm.date) {
      setWfErr('Please enter a date.');
      return;
    }
    if (!wfForm.teamMember.trim()) {
      setWfErr('Please enter your name.');
      return;
    }
    if (!wfForm.batchId) {
      setWfErr('Please select a pig group.');
      return;
    }
    // Check custom required fields
    for (const f of wfRequiredFields) {
      if (f.system) continue; // already checked above
      if (f.id === 'pig_count' && wfForm.pigCount === '') {
        setWfErr(`${String(f.label)} is required.`);
        return;
      }
      if (f.id === 'feed_lbs' && wfForm.feedLbs === '') {
        setWfErr(`${String(f.label)} is required.`);
        return;
      }
      if (f.id === 'fence_voltage' && wfForm.fenceVoltage === '') {
        setWfErr(`${String(f.label)} is required.`);
        return;
      }
      if (f.id === 'issues' && !wfForm.issues.trim()) {
        setWfErr(`${String(f.label)} is required.`);
        return;
      }
    }
    setWfErr('');
    setWfSubmitting(true);

    // Validate the photo count cap upfront — DailyPhotoCapture also enforces
    // it, the hook's preparePhotos throws on >10 too, but a defense-in-depth
    // check produces a clean inline error rather than a thrown range error.
    if (wfPhotos.length > MAX_PHOTOS_PER_REPORT) {
      setWfSubmitting(false);
      setWfErr(`Up to ${MAX_PHOTOS_PER_REPORT} photos per submission.`);
      return;
    }

    // Phase 1D-A: single submit path — hook decides flat vs hasPhotos based
    // on payload.photos. Empty/absent photos → flat insert (existing 1C-B
    // behavior, locked by pig_dailys_offline.spec.js). 1+ photo → prepared-
    // photo flow with atomic queue/stuck enqueue on failure.
    setWfPhotoStatuses(wfPhotos.length > 0 ? wfPhotos.map(() => 'pending') : []);
    recoveryOperation.current = 'submitting';
    try {
      const payload = buildSubmitPayload();
      if (wfPhotos.length > 0) {
        payload.photos = wfPhotos; // raw File[] — hook compresses + sanitizes
      }
      const result = await submit(payload, recovery ? {replacesCsid: recovery.csid} : undefined);
      setRecovery(null);
      priorDraft.current = null;
      setWfGroupName(wfForm.batchId);
      // result.state ∈ {'synced', 'queued', 'stuck'}.
      setWfDoneState(result.state);
      // Reflect outcome on the per-photo chips so DailyPhotoCapture's UI
      // shows the right pill in the success/queued/stuck screen if any
      // were rendered. (The terminal screens don't render chips, but
      // status state stays consistent for any test that inspects it.)
      if (wfPhotos.length > 0) {
        if (result.state === 'synced') {
          setWfPhotoStatuses(wfPhotos.map(() => 'uploaded'));
        } else if (result.state === 'queued') {
          setWfPhotoStatuses(wfPhotos.map(() => 'queued'));
        } else if (result.state === 'stuck') {
          setWfPhotoStatuses(wfPhotos.map(() => 'failed'));
          // Auto-open stuck modal so the operator sees what went wrong.
          setWfStuckOpen(true);
        }
      }
    } catch (e) {
      // useOfflineSubmit throws on schema/validation errors that occur
      // BEFORE photos enter the bucket (preparePhotos errors, no-photo
      // schema errors). Photo-after-upload schema errors do NOT throw
      // here — they're captured into state:'stuck' instead.
      setWfErr('Could not save: ' + errorMessage(e));
      if (wfPhotos.length > 0) {
        setWfPhotoStatuses(wfPhotos.map(() => 'failed'));
      }
    } finally {
      recoveryOperation.current = null;
      setWfSubmitting(false);
    }
  }

  function wfReset() {
    setWfForm({
      date: todayCentralISO(),
      teamMember: sessionSubmitter?.name || '',
      batchId: '',
      pigCount: '',
      feedLbs: '',
      groupMoved: true,
      nippleDrinkerMoved: true,
      nippleDrinkerWorking: true,
      troughsMoved: true,
      fenceWalked: true,
      fenceVoltage: '',
      issues: '',
    });
    setWfPhotos([]);
    setWfPhotoStatuses([]);
    setWfDoneState('none');
    setWfErr('');
  }

  function wfTgl(label: string, field: PigDailyToggleField) {
    return (
      <div style={{marginBottom: 12}}>
        <label style={{display: 'block', fontSize: 12, color: 'var(--ink-muted)', marginBottom: 4, fontWeight: 500}}>
          {label}
        </label>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            borderRadius: 10,
            overflow: 'hidden',
            border: '1px solid var(--border-strong)',
          }}
        >
          {[
            {v: true, l: 'Yes'},
            {v: false, l: 'No'},
          ].map(({v, l}) => (
            <button
              key={String(v)}
              type="button"
              onClick={() => wfToggle(field, v)}
              style={{
                padding: '9px 0',
                border: 'none',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                background: 'white',
                color: wfForm[field] === v ? 'var(--brand)' : 'var(--ink-muted)',
              }}
            >
              {l}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const wfCfgFields = configuredFields(webformsConfig, 'pig-dailys');
  const isReq = (id: string) => {
    const f = wfCfgFields.find((f) => f.id === id);
    return f ? Boolean(f.required) : ['date', 'team_member', 'group'].includes(id);
  };
  const wfLbl = (text: string, id: string) => (
    <label style={{display: 'block', fontSize: 12, color: 'var(--ink-muted)', marginBottom: 4, fontWeight: 500}}>
      {text}
      {isReq(id) && <span style={{color: '#b91c1c', marginLeft: 2}}>*</span>}
    </label>
  );

  const wfCard = (title: React.ReactNode, children: React.ReactNode) => (
    <div
      style={{
        background: 'white',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: 20,
        marginBottom: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,.06)',
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: 'var(--ink-muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          marginBottom: 14,
          paddingBottom: 10,
          borderBottom: '1px solid var(--border)',
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );

  // Phase 1D-A — stuck modal lifted to a shared element so it renders on
  // BOTH the form screen AND the success/queued/stuck terminal screen
  // (matches WeighInsWebform's pattern). Auto-open + manual-open from the
  // 'stuck' state both flow through wfStuckOpen.
  const stuckModalEl =
    wfStuckOpen && stuckRows.length > 0 ? (
      <StuckSubmissionsModal
        onOpen={openQueued}
        busy={recoveryBusy}
        error={wfErr}
        rows={stuckRows}
        formLabel="pig daily report"
        describeRow={(row) => {
          const rec = isRecord(row.record) ? row.record : row.payload;
          const batchLabel = payloadText(rec, 'batch_label', '');
          const label = batchLabel || payloadText(rec, 'batch_id');
          const date = payloadText(rec, 'date');
          const photos = payloadMember(rec, 'photos');
          const photoCount = Array.isArray(photos) ? photos.length : 0;
          const photoSuffix = photoCount > 0 ? ` · ${photoCount} photo${photoCount === 1 ? '' : 's'}` : '';
          return `${label} · ${date}${photoSuffix} (not yet sent)`;
        }}
        onRetry={async (csid) => {
          await retryStuck(csid);
        }}
        onDiscard={async (csid) => {
          await discardStuck(csid);
        }}
        onClose={() => setWfStuckOpen(false)}
      />
    ) : null;

  if (wfDoneState !== 'none')
    return (
      <div style={{background: 'var(--bg-page)', minHeight: '100vh'}}>
        <div
          style={{
            background: 'linear-gradient(135deg,#042f23,#085041 60%,#0d6652)',
            color: 'white',
            padding: '14px 1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
            <div style={{fontSize: 17, fontWeight: 700, letterSpacing: '-.4px'}}>WCF Planner</div>
            <span
              style={{
                fontSize: 11,
                fontWeight: 500,
                color: 'rgba(255,255,255,.6)',
                borderLeft: '1px solid rgba(255,255,255,.25)',
                paddingLeft: 10,
                letterSpacing: 0.5,
              }}
            >
              PIGS
            </span>
          </div>
          <div style={{fontSize: 12, color: 'rgba(255,255,255,.6)'}}>Daily Report</div>
        </div>
        <div style={{maxWidth: 540, margin: '0 auto', padding: '3rem 1rem', textAlign: 'center'}}>
          <div style={{fontSize: 56, marginBottom: 16}}>
            {wfDoneState === 'queued' ? '📡' : wfDoneState === 'stuck' ? '⚠️' : '✅'}
          </div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              marginBottom: 8,
              color: wfDoneState === 'queued' ? '#92400e' : wfDoneState === 'stuck' ? '#b91c1c' : 'var(--ink)',
            }}
          >
            {wfDoneState === 'queued'
              ? 'Saved on this device'
              : wfDoneState === 'stuck'
                ? 'Saved on this device — sync needs help'
                : 'Report submitted!'}
          </div>
          {wfDoneState === 'queued' && (
            <div
              data-submit-state="queued"
              style={{
                fontSize: 13,
                color: '#78716c',
                marginBottom: 22,
                lineHeight: 1.5,
                background: '#fef3c7',
                border: '1px solid #fde68a',
                borderRadius: 10,
                padding: '10px 14px',
                textAlign: 'left',
              }}
            >
              No connection right now. Daily report queued for <strong>{wfGroupName}</strong> and will sync as soon as
              the device is back online.
            </div>
          )}
          {wfDoneState === 'stuck' && (
            <div
              data-submit-state="stuck"
              style={{
                fontSize: 13,
                color: '#7f1d1d',
                marginBottom: 22,
                lineHeight: 1.5,
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 10,
                padding: '10px 14px',
                textAlign: 'left',
              }}
            >
              Your daily report for <strong>{wfGroupName}</strong> is saved on this device, but sync hit an error that
              won't fix itself by retrying. Open the stuck submissions panel to retry after a fix or discard.
            </div>
          )}
          {wfDoneState === 'synced' && (
            <div style={{fontSize: 14, color: 'var(--ink-muted)', marginBottom: 28}}>
              <span data-submit-state="synced" style={{display: 'none'}}>
                synced
              </span>
              Daily report saved for <strong>{wfGroupName}</strong>.
            </div>
          )}
          <button
            onClick={wfReset}
            style={{
              padding: '10px 28px',
              border: '2px solid #085041',
              borderRadius: 10,
              background: 'white',
              color: '#085041',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Submit another
          </button>
          {wfDoneState === 'stuck' && (
            <button
              onClick={() => setWfStuckOpen(true)}
              data-stuck-open="1"
              style={{
                marginLeft: 12,
                padding: '10px 22px',
                border: '2px solid #b91c1c',
                borderRadius: 10,
                background: 'white',
                color: '#b91c1c',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Open stuck submissions
            </button>
          )}
        </div>
        {stuckModalEl}
      </div>
    );

  return (
    <div style={{background: 'var(--bg-page)', minHeight: '100vh'}}>
      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(135deg,#042f23,#085041 60%,#0d6652)',
          color: 'white',
          padding: '14px 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
          <div style={{fontSize: 17, fontWeight: 700, letterSpacing: '-.4px'}}>WCF Planner</div>
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: 'rgba(255,255,255,.6)',
              borderLeft: '1px solid rgba(255,255,255,.25)',
              paddingLeft: 10,
              letterSpacing: 0.5,
            }}
          >
            PIGS
          </span>
        </div>
        <div style={{fontSize: 12, color: 'rgba(255,255,255,.6)'}}>Daily Report</div>
      </div>

      <div style={{maxWidth: 540, margin: '0 auto', padding: '1.5rem 1rem 3rem'}}>
        <div style={{fontSize: 22, fontWeight: 700, color: 'var(--ink)', marginBottom: 20, letterSpacing: '-.3px'}}>
          Pig Dailys
        </div>

        {stuckRows.length > 0 && (
          <button
            onClick={() => setWfStuckOpen(true)}
            data-stuck-button="1"
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: 10,
              border: '1px solid #fde68a',
              background: '#fef3c7',
              color: '#92400e',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginBottom: 12,
              textAlign: 'left',
            }}
          >
            ⚠ {stuckRows.length} unsynced pig daily report{stuckRows.length === 1 ? '' : 's'} — tap to review
          </button>
        )}

        {recovery && (
          <div role="status" data-queued-recovery={recovery.csid} style={{marginBottom: 12, color: 'var(--warn-ink)'}}>
            Review these saved answers and photos before submitting a replacement. The original stays on this device
            until the replacement is saved.
            {recovery.legacyIdentity && (
              <p>The original account is unknown. Submitting will create a new report as {lockedName}.</p>
            )}
            <button type="button" onClick={cancelRecovery} disabled={recoveryBusy}>
              Cancel recovery
            </button>
          </div>
        )}
        {wfErr && (
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 10,
              color: '#b91c1c',
              padding: '10px 14px',
              fontSize: 13,
              marginBottom: 14,
            }}
          >
            {wfErr}
          </div>
        )}

        {wfCard(
          'Report Info',
          <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
            <div>
              <label
                style={{display: 'block', fontSize: 12, color: 'var(--ink-muted)', marginBottom: 4, fontWeight: 500}}
              >
                Date{isReq('date') && <span style={{color: '#b91c1c', marginLeft: 2}}>*</span>}
              </label>
              <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                <input
                  type="date"
                  value={wfForm.date}
                  onChange={(e) => setWfForm({...wfForm, date: e.target.value})}
                  style={{
                    fontFamily: 'inherit',
                    fontSize: 13,
                    padding: '9px 12px',
                    border: '1px solid var(--border-strong)',
                    borderRadius: 10,
                    flex: 1,
                    outline: 'none',
                    background: 'white',
                    color: 'var(--ink)',
                  }}
                />
                <span
                  onClick={() => {
                    setWfForm({
                      ...wfForm,
                      date: todayCentralISO(),
                    });
                  }}
                  style={{
                    display: 'inline-block',
                    fontSize: 11,
                    padding: '6px 10px',
                    background: '#ecfdf5',
                    color: '#085041',
                    border: '1px solid #a7f3d0',
                    borderRadius: 10,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Today
                </span>
              </div>
            </div>
            <div>
              <LockedSubmitter
                name={lockedName}
                label="Team member"
                labelStyle={{
                  display: 'block',
                  fontSize: 12,
                  color: 'var(--ink-muted)',
                  marginBottom: 4,
                  fontWeight: 500,
                }}
              />
            </div>
            <div>
              <label
                style={{display: 'block', fontSize: 12, color: 'var(--ink-muted)', marginBottom: 4, fontWeight: 500}}
              >
                Pig group{isReq('group') && <span style={{color: '#b91c1c', marginLeft: 2}}>*</span>}
              </label>
              <select
                value={wfForm.batchId}
                onChange={(e) => setWfForm({...wfForm, batchId: e.target.value})}
                style={{
                  fontFamily: 'inherit',
                  fontSize: 13,
                  padding: '9px 12px',
                  border: '1px solid var(--border-strong)',
                  borderRadius: 10,
                  width: '100%',
                  outline: 'none',
                  background: 'white',
                  color: 'var(--ink)',
                }}
              >
                <option value="">— Select group —</option>
                {recovery && wfForm.batchId && !wfGroupOptions.some((g) => g.value === wfForm.batchId) && (
                  <option value={wfForm.batchId}>{wfForm.batchId} (saved report)</option>
                )}
                {wfGroupOptions.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>
          </div>,
        )}

        {wfCard(
          'Count & Feed',
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
            <div>
              <label
                style={{display: 'block', fontSize: 12, color: 'var(--ink-muted)', marginBottom: 4, fontWeight: 500}}
              >
                # Pigs in group{isReq('pig_count') && <span style={{color: '#b91c1c', marginLeft: 2}}>*</span>}
              </label>
              <input
                type="number"
                min="0"
                value={wfForm.pigCount || ''}
                onChange={(e) => setWfForm({...wfForm, pigCount: e.target.value})}
                placeholder="0"
                style={{
                  fontFamily: 'inherit',
                  fontSize: 13,
                  padding: '9px 12px',
                  border: '1px solid var(--border-strong)',
                  borderRadius: 10,
                  width: '100%',
                  outline: 'none',
                  background: 'white',
                }}
              />
              <div style={{fontSize: 11, color: 'var(--ink-faint)', marginTop: 3}}>Current headcount</div>
            </div>
            <div>
              <label
                style={{display: 'block', fontSize: 12, color: 'var(--ink-muted)', marginBottom: 4, fontWeight: 500}}
              >
                Feed given (lbs){isReq('feed_lbs') && <span style={{color: '#b91c1c', marginLeft: 2}}>*</span>}
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={wfForm.feedLbs || ''}
                onChange={(e) => setWfForm({...wfForm, feedLbs: e.target.value})}
                placeholder="0"
                style={{
                  fontFamily: 'inherit',
                  fontSize: 13,
                  padding: '9px 12px',
                  border: '1px solid var(--border-strong)',
                  borderRadius: 10,
                  width: '100%',
                  outline: 'none',
                  background: 'white',
                }}
              />
              <div style={{fontSize: 11, color: 'var(--ink-faint)', marginTop: 3}}>Total lbs fed today</div>
            </div>
          </div>,
        )}

        {wfCard(
          'Daily Checks',
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
            {wfTgl('Was group moved?', 'groupMoved')}
            {wfTgl('Nipple drinker moved?', 'nippleDrinkerMoved')}
            {wfTgl('Nipple drinker working?', 'nippleDrinkerWorking')}
            {wfTgl('Feed troughs moved?', 'troughsMoved')}
            {wfTgl('Fence line walked?', 'fenceWalked')}
            <div>
              <label
                style={{display: 'block', fontSize: 12, color: 'var(--ink-muted)', marginBottom: 4, fontWeight: 500}}
              >
                Fence voltage (kV){isReq('fence_voltage') && <span style={{color: '#b91c1c', marginLeft: 2}}>*</span>}
              </label>
              <input
                type="number"
                min="0"
                max="20"
                step="0.1"
                value={wfForm.fenceVoltage || ''}
                onChange={(e) => setWfForm({...wfForm, fenceVoltage: e.target.value})}
                placeholder="e.g. 4.2"
                style={{
                  fontFamily: 'inherit',
                  fontSize: 13,
                  padding: '9px 12px',
                  border: '1px solid var(--border-strong)',
                  borderRadius: 10,
                  width: '100%',
                  outline: 'none',
                  background: 'white',
                }}
              />
            </div>
          </div>,
        )}

        {wfCard(
          'Issues & Comments',
          <div>
            <label
              style={{display: 'block', fontSize: 12, color: 'var(--ink-muted)', marginBottom: 4, fontWeight: 500}}
            >
              Notes, issues, observations{isReq('issues') && <span style={{color: '#b91c1c', marginLeft: 2}}>*</span>}
            </label>
            <textarea
              rows={4}
              value={wfForm.issues}
              onChange={(e) => setWfForm({...wfForm, issues: e.target.value})}
              placeholder="Any problems, unusual behavior, health concerns, maintenance needed…"
              style={{
                fontFamily: 'inherit',
                fontSize: 13,
                padding: '9px 12px',
                border: '1px solid var(--border-strong)',
                borderRadius: 10,
                width: '100%',
                outline: 'none',
                background: 'white',
                resize: 'vertical',
              }}
            />
          </div>,
        )}

        <DailyPhotoCapture
          files={wfPhotos}
          statuses={wfPhotoStatuses}
          onChange={setWfPhotos}
          disabled={recoveryBusy}
          offlineCapable={true}
        />

        <button
          onClick={wfSubmit}
          disabled={recoveryBusy}
          style={{
            width: '100%',
            padding: 13,
            border: 'none',
            borderRadius: 10,
            background: 'linear-gradient(135deg,#085041,#0d6652)',
            color: 'white',
            fontSize: 15,
            fontWeight: 600,
            cursor: wfSubmitting ? 'not-allowed' : 'pointer',
            opacity: wfSubmitting ? 0.6 : 1,
            boxShadow: '0 2px 8px rgba(8,80,65,.25)',
            fontFamily: 'inherit',
          }}
        >
          {wfSubmitting ? 'Submitting…' : 'Submit Daily Report'}
        </button>
      </div>

      {stuckModalEl}
    </div>
  );
}
